import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';

const ImagesTab = ({ productData, onFieldChange }) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [newImageUrl, setNewImageUrl] = useState('');

  const images = productData?.images || [];

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
    },
    maxFiles: 12,
    onDrop: (acceptedFiles) => {
      // In a real app, upload to storage and get URLs
      const newImages = acceptedFiles?.map(file => URL.createObjectURL(file));
      onFieldChange('images', [...images, ...newImages]);
    }
  });

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
  };

  const handleDrop = (dropIndex) => {
    if (draggedIndex === null) return;

    const updatedImages = [...images];
    const [draggedImage] = updatedImages?.splice(draggedIndex, 1);
    updatedImages?.splice(dropIndex, 0, draggedImage);

    onFieldChange('images', updatedImages);
    setDraggedIndex(null);
  };

  const handleRemoveImage = (index) => {
    const updatedImages = [...images];
    updatedImages?.splice(index, 1);
    onFieldChange('images', updatedImages);
  };

  const handleAddImageUrl = () => {
    if (!newImageUrl?.trim()) return;
    
    onFieldChange('images', [...images, newImageUrl?.trim()]);
    setNewImageUrl('');
  };

  return (
    <div className="space-y-6">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-smooth
          ${isDragActive 
            ? 'border-primary bg-primary/5' :'border-border hover:border-primary hover:bg-muted/50'
          }
        `}
      >
        <input {...getInputProps()} />
        <Icon name="Upload" size={40} className="mx-auto mb-3 text-muted-foreground" />
        <p className="text-foreground font-medium mb-1">
          {isDragActive ? 'Solte as imagens aqui' : 'Arraste imagens ou clique para selecionar'}
        </p>
        <p className="text-sm text-muted-foreground">
          Até 12 imagens (PNG, JPG, GIF, WebP)
        </p>
      </div>

      {/* Add by URL */}
      <div className="flex gap-2">
        <Input
          placeholder="Ou cole a URL da imagem"
          value={newImageUrl}
          onChange={(e) => setNewImageUrl(e?.target?.value)}
        />
        <Button
          iconName="Plus"
          onClick={handleAddImageUrl}
        >
          Adicionar
        </Button>
      </div>

      {/* Images Grid */}
      {images?.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images?.map((image, index) => (
            <div
              key={index}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(index)}
              className="relative group aspect-square bg-muted rounded-lg overflow-hidden border border-border cursor-move"
            >
              <img
                src={image}
                alt={`Product ${index + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = '/assets/images/no_image.png';
                }}
              />
              
              {/* Principal Badge */}
              {index === 0 && (
                <div className="absolute top-2 left-2 px-2 py-1 bg-primary text-white text-xs font-medium rounded">
                  Principal
                </div>
              )}
              
              {/* Remove Button */}
              <button
                onClick={() => handleRemoveImage(index)}
                className="absolute top-2 right-2 p-1.5 bg-destructive text-white rounded opacity-0 group-hover:opacity-100 transition-smooth"
              >
                <Icon name="Trash2" size={14} />
              </button>
              
              {/* Drag Handle */}
              <div className="absolute bottom-2 right-2 p-1.5 bg-black/50 text-white rounded opacity-0 group-hover:opacity-100 transition-smooth">
                <Icon name="GripVertical" size={14} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Video URL */}
      <Input
        label="URL do Vídeo (YouTube/Vimeo)"
        value={productData?.videoUrl}
        onChange={(e) => onFieldChange('videoUrl', e?.target?.value)}
        placeholder="https://youtube.com/watch?v=..."
        description="Opcional: adicione um vídeo demonstrativo"
      />
    </div>
  );
};

export default ImagesTab;